import { useState } from "react";
import { Save, Plus, Pencil, Trash2, Receipt, Undo2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getApiErrorMessage } from "@/lib/apiClient";

import { useMedicoes, useMedicaoMutations } from "@/features/projetos/hooks/useFinanceiro";
import { STATUS_MEDICAO } from "@/features/projetos/types";

const fmt = (v: number) => Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtData = (d: string | null) => (d ? d.slice(0, 10).split("-").reverse().join("/") : "—");
const isoLocal = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const hojeISO = () => isoLocal(new Date());
const venc30 = () => { const d = new Date(); d.setDate(d.getDate() + 30); return isoLocal(d); };

const STATUS_CLASSE: Record<number, string> = {
  0: "bg-amber-100 text-amber-700",
  1: "bg-green-100 text-green-700",
  2: "bg-slate-100 text-slate-600",
};

export default function ProjetoFaturamentoTab({ projetoId }: { projetoId: number }) {
  const { data: medicoes = [] } = useMedicoes(projetoId);
  const { salvar, excluir, faturar, cancelar } = useMedicaoMutations(projetoId);

  const [dtVencimento, setDtVencimento] = useState(venc30());
  const [estornarId, setEstornarId] = useState<number | null>(null);

  const [editId, setEditId] = useState<number | null>(null);
  const [descricao, setDescricao] = useState("");
  const [dtMedicao, setDtMedicao] = useState(hojeISO());
  const [valor, setValor] = useState("0");
  const limpar = () => { setEditId(null); setDescricao(""); setDtMedicao(hojeISO()); setValor("0"); };

  const totalFaturado = medicoes.filter(m => m.status === 1).reduce((s, m) => s + Number(m.valor), 0);
  const totalPendente = medicoes.filter(m => m.status === 0).reduce((s, m) => s + Number(m.valor), 0);

  const salvarMed = async () => {
    if (Number(valor) <= 0) { toast.error("Informe o valor (> 0)."); return; }
    try {
      await salvar.mutateAsync({ medId: editId ?? undefined, req: { descricao, dtMedicao, valor: Number(valor) } });
      toast.success("Medição salva."); limpar();
    } catch (e) { toast.error(getApiErrorMessage(e, "Erro ao salvar medição.")); }
  };
  const removerMed = async (id: number) => {
    try { await excluir.mutateAsync(id); toast.success("Medição removida."); if (editId === id) limpar(); }
    catch (e) { toast.error(getApiErrorMessage(e, "Erro ao remover medição.")); }
  };
  const faturarMed = async (id: number) => {
    try {
      const r = await faturar.mutateAsync({ medId: id, dtVencimento });
      toast.success(r.mensagem);
    } catch (e) { toast.error(getApiErrorMessage(e, "Não foi possível faturar a medição.")); }
  };
  const estornarMed = async () => {
    if (estornarId == null) return;
    try { await cancelar.mutateAsync(estornarId); toast.success("Faturamento estornado (título cancelado)."); }
    catch (e) { toast.error(getApiErrorMessage(e, "Não foi possível estornar.")); }
    finally { setEstornarId(null); }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Receipt className="h-4 w-4" /> Medições e faturamento</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            <div className="rounded-lg border p-3"><div className="text-xs text-muted-foreground">Total faturado</div><div className="text-lg font-semibold tabular-nums text-green-700">{fmt(totalFaturado)}</div></div>
            <div className="rounded-lg border p-3"><div className="text-xs text-muted-foreground">Total pendente</div><div className="text-lg font-semibold tabular-nums text-amber-700">{fmt(totalPendente)}</div></div>
            <div className="rounded-lg border p-3">
              <Label className="text-xs text-muted-foreground">Vencimento (ao faturar)</Label>
              <Input type="date" value={dtVencimento} onChange={e => setDtVencimento(e.target.value)} className="mt-1" />
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-3 border-t pt-4">
            <div className="min-w-[220px] flex-1"><Label>Descrição</Label><Input value={descricao} onChange={e => setDescricao(e.target.value)} maxLength={200} placeholder="Ex.: Marco 1 - entrega parcial" /></div>
            <div><Label>Data da medição</Label><Input type="date" value={dtMedicao} onChange={e => setDtMedicao(e.target.value)} /></div>
            <div className="w-36"><Label>Valor</Label><Input type="number" step="0.01" value={valor} onChange={e => setValor(e.target.value)} /></div>
            <Button onClick={salvarMed}>{editId ? <><Save className="mr-2 h-4 w-4" />Atualizar</> : <><Plus className="mr-2 h-4 w-4" />Adicionar</>}</Button>
            {editId && <Button variant="outline" onClick={limpar}>Cancelar</Button>}
          </div>

          <Table>
            <TableHeader><TableRow>
              <TableHead>Descrição</TableHead><TableHead>Data</TableHead><TableHead className="text-right">Valor</TableHead>
              <TableHead>Status</TableHead><TableHead>Título</TableHead><TableHead className="w-44 text-right">Ações</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {medicoes.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Nenhuma medição.</TableCell></TableRow>
              ) : medicoes.map(m => (
                <TableRow key={m.id}>
                  <TableCell className="max-w-[260px] truncate">{m.descricao}</TableCell>
                  <TableCell>{fmtData(m.dtMedicao)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(m.valor)}</TableCell>
                  <TableCell><Badge variant="outline" className={STATUS_CLASSE[m.status] ?? ""}>{STATUS_MEDICAO[m.status] ?? m.status}</Badge></TableCell>
                  <TableCell>{m.contaReceberId > 0 ? `#${m.contaReceberId}` : "—"}</TableCell>
                  <TableCell className="text-right">
                    {m.status === 0 ? (
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" onClick={() => faturarMed(m.id)}><Receipt className="mr-1 h-3.5 w-3.5" />Faturar</Button>
                        <Button variant="ghost" size="icon" onClick={() => { setEditId(m.id); setDescricao(m.descricao ?? ""); setDtMedicao(m.dtMedicao.slice(0, 10)); setValor(String(m.valor)); }}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => removerMed(m.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      </div>
                    ) : m.status === 1 ? (
                      <div className="flex items-center justify-end gap-2">
                        <span className="text-xs text-muted-foreground">{fmtData(m.dtFaturamento)}</span>
                        <Button variant="ghost" size="sm" onClick={() => setEstornarId(m.id)}><Undo2 className="mr-1 h-3.5 w-3.5 text-destructive" />Estornar</Button>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">Cancelada</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <p className="text-xs text-muted-foreground">Ao faturar, é gerado um título em Contas a Receber para o cliente do projeto (1 parcela, vencimento acima).</p>
        </CardContent>
      </Card>

      <AlertDialog open={estornarId != null} onOpenChange={o => !o && setEstornarId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Estornar faturamento?</AlertDialogTitle>
            <AlertDialogDescription>
              A medição será cancelada e o título de Contas a Receber será cancelado (caso ainda não tenha sido recebido/baixado). Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction onClick={estornarMed}>Estornar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
